import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { take, catchError, switchMap, map } from 'rxjs/operators';
import { ExpectedOwnedStrategy } from 'src/app/shared/owned-strategy-card/strategy';
import { UserService } from '../user/user.service';
import {
  ApiModelStrategy,
  ModelStrategyOrPortfolioInstrumentOut,
  ApiClientAccountOut,
  ApiAvailableCash,
  ApiPagedTransaction,
  PaginatedTransactionInput,
  PortfolioOut,
  ApiModelPortfolioOut,
  PortfolioDepositOut,
  ApiSummaryChartOut,
  StrategyLimitOut,
  SummaryChartInput,
  ApiSummarySum,
  InstrumentPredictionOut,
  ApiColCollaboratorOut,
  ColClientAccountDetailOut,
  ClientModelPortfolioChartInput,
  ApiStrategyDraftInput,
  ApiPortfolioDraftInput,
  DraftPortfolioOut,
  InstrumentDetailFullOut,
  FrameworkContractFeeSettingsOut,
  CollaboratorOut,
  ColSummaryOut,
  PagedResultColClientAccountOverviewOut,
  PortfolioInstrumentSummaryOut,
  ColClientsInput,
  ColClientAccountStatisticOut,
  ColClientsMissingDepositInput,
  ColClientMissingRegularPage,
  ColClientExpiringContractsPage,
  EntryFeeCalculationWithAllRatiosOut,
  EntryFeeCalculationIn,
  ClientRequestInput,
  PortfolioFeeConfigsOut,
  FeeConfigOut,
  ColCollaboratorIdClientAccountIdOut,
  ApiModelPortfolioChartOut,
  AccessToken,
  investmentFrequencyMap,
  InvestmentSuggestionPdfInput,
  ClientParams,
  ModelStrategyChartInput,
  ColClientAccountOpfTransferOut,
  ApiRegularWithdrawal,
  RegularWithdrawalDraftIn,
  ColClientsRegularWithdrawalAvailableInput,
  ApiRegularWithdrawalLimit,
  WithdrawalDraftLimitsIn,
  ClientBankAccountOut,
  BankAccountsIn,
  ApiWithdrawalPortfolio,
  GenerateDocumentLinkOut,
  ExportTransactionsIn,
  PagedResultDocumentOut,
  DocumentsRequestInput,
  AllInvestmentsListItem,
  DraftPortfolioDetail,
  DraftPortfolioDetailInput,
  CollaboratorDraftPortfoliosInput,
  PagedResultDraftPortfolioOverviewItemOut,
  DraftTemplateIn,
  DraftTemplateOut,
  StrategyTemplateDetailOut,
  TemplateDraftsListInput,
  PagedResultStrategyTemplateOverviewOut,
} from './rest-api';
import { ConfigService } from '../json-config.service';
import { EnvironmentKeys } from 'src/environments/environment-keys';
import { Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { desktopWidthBreakpoint } from 'src/app/shared/constants';
import { PdfDataInput } from '../pdf-suggestion.service';

@Injectable({
  providedIn: 'root'
})
export class RestApiService {
  private get appHeaders() {
    return new HttpHeaders({
      'accept': 'application/json',
      'Authorization': 'Bearer ' + this.userService.bearer,
    });
  }

  private toHttpParams(paramsObj?: Record<string, unknown>): HttpParams {
    let params = new HttpParams();
    if(paramsObj) {
      Object.entries(paramsObj).forEach(([key, value]) => {
        if (value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => params = params.append(key, String(v)));
          } else {
            params = params.set(key, String(value));
          }
        }
      });
    }
    return params;
  }

  private get loggedConsult(): ApiColCollaboratorOut | null {
    if(!this.userService.loggedConsult || this.userService.loggedConsult === null) {
      this.router.navigate(['/login']);
    }

    return this.userService.loggedConsult;
  }

  public constructor(
    private http: HttpClient,
    private userService: UserService,
    private configService: ConfigService,
    private router: Router,
    private sanitizer: DomSanitizer,
  ) { }

  // GET /api/v2/client/{clientAccountId}/summary
  public getSummaryWithPortfolios(params: { clientId: number, currencyCode: string}): Observable<ApiSummarySum> {
    return this.http
      .get<ApiSummarySum>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v2/client/${params.clientId}/summary?currencyCode=${params.currencyCode}`,
        {
          headers: this.appHeaders,
        },
      )
      .pipe(
        take(1),
        switchMap((summary: ApiSummarySum) => {
          summary.portfolios.forEach(
            (p) =>{
              p.weeksBetweenInvestments = investmentFrequencyMap.get(
                p.investmentFrequency
              )!;
              p.isDraft = false;
          });
          return of(summary);
        }),
        catchError(this.handleError.bind(this)),
      );
  }

  public getPortfoliosAndStrategies(params: ClientParams): Observable<ExpectedOwnedStrategy[]> {
    return this.getSummaryWithPortfolios(params)
    .pipe(
      switchMap(res => of(res.portfolios))
    )
  }

  // GET /api/v1/client/{clientAccountId}/regular-withdrawal/portfolio
  public getWithdrawalPortfolios(clientAccountId: string | number): Observable<ApiWithdrawalPortfolio[]> {
    return this.http
    .get<ApiWithdrawalPortfolio[]>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/client/' + clientAccountId + '/regular-withdrawal/portfolio',
      {
        headers: this.appHeaders,
      },
    )
    .pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/client/{clientAccountId}/summary/graph
  public getSummaryChart(params: SummaryChartInput): Observable<ApiSummaryChartOut> {
    const defaultTicks = window.innerWidth >= desktopWidthBreakpoint ? 100 : 25;

    return this.http
      .get<ApiSummaryChartOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${params.clientId}/summary/graph`,
        {
          headers: this.appHeaders,
          params: {
            currencyCode: params.currencyCode,
            fromDate: params.fromDate?.toISOString().slice(0, 10) ?? '',
            toDate: params.toDate?.toISOString().slice(0, 10) ?? '',
            size: params.size ?? defaultTicks,
            priceMeasure: params.priceMeasure ?? 'VALUE',
          },
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/graph
  public getInvestmentChart(params: SummaryChartInput, portfolioId: string): Observable<ApiSummaryChartOut> {
    const defaultTicks = window.innerWidth >= desktopWidthBreakpoint ? 100 : 25;
    return this.http
      .get<ApiSummaryChartOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${params.clientId}/portfolio/${portfolioId}/graph`,
        {
          headers: this.appHeaders,
          params: {
            currencyCode: params.currencyCode,
            fromDate: params.fromDate?.toISOString().slice(0, 10) ?? '',
            toDate: params.toDate?.toISOString().slice(0, 10) ?? '',
            size: params.size ?? defaultTicks,
            priceMeasure: params.priceMeasure ?? 'VALUE',
          },
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/instrument/cash
  public getAvailableCash(): Observable<ApiAvailableCash> {
    return this.http
      .get<ApiAvailableCash>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/instrument/cash`,
        {
          headers: this.appHeaders,
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v2/collaborator/{collaboratorId}/clientAccount/{clientAccountId}/frameworkContract/{frameworkContractId}/strategy
  public postStrategyConsultDraft(draftInput: ApiStrategyDraftInput): Observable<DraftPortfolioOut> {
    return this.http
      .post<DraftPortfolioOut>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) +  'v2/collaborator/' + draftInput.collaboratorId + '/clientAccount/' + draftInput.clientAccountId + '/frameworkContract/' + draftInput.frameworkContractId + '/strategy',
        {
          ...draftInput.investmentDraft
        },
        {
          headers: this.appHeaders,
        }
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

    // POST /api/v1/collaborator/{collaboratorId}/clientAccount/{clientAccountId}/frameworkContract/{frameworkContractId}/efektika
  public postEfektikaStrategyConsultDraft(draftInput: ApiStrategyDraftInput): Observable<DraftPortfolioOut> {
    return this.http
      .post<DraftPortfolioOut>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) +  'v1/collaborator/' + draftInput.collaboratorId + '/clientAccount/' + draftInput.clientAccountId + '/frameworkContract/' + draftInput.frameworkContractId + '/efektika',
        {
          ...draftInput.investmentDraft
        },
        {
          headers: this.appHeaders,
        }
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v2/collaborator/{collaboratorId}/clientAccount/{clientAccountId}/frameworkContract/{frameworkContractId}/portfolio
  public postPortfolioConsultDraft(draftInput: ApiPortfolioDraftInput): Observable<DraftPortfolioOut> {
    return this.http
      .post<DraftPortfolioOut>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) +  'v2/collaborator/' + draftInput.collaboratorId + '/clientAccount/' + draftInput.clientAccountId + '/frameworkContract/' + draftInput.frameworkContractId + '/portfolio',
        {
          ...draftInput.investmentDraft
        },
        {
          headers: this.appHeaders,
        }
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/instrument/detail
  public getInstruments(canReadEnglishOverride?: boolean): Observable<InstrumentDetailFullOut[]> {
    return this.http
      .get<InstrumentDetailFullOut[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/instrument/detail`,
        {
          headers: this.appHeaders,
          params: {
            withIsInEnglish: canReadEnglishOverride ?? false,
          }
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/collaborator/{collaboratorId}/clientAccount/{clientAccountId}/instrument/detail
  public getConsultInstruments(clientAccountId: string | number): Observable<InstrumentDetailFullOut[]> {
    return this.http
      .get<InstrumentDetailFullOut[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/collaborator/${this.loggedConsult?.collaboratorId}/clientAccount/${clientAccountId}/instrument/detail`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/modelStrategy
  public getModelStrategies(overrideCanReadEnglish = false, efektika = false): Observable<ApiModelStrategy[]> {
    return this.http
      .get<ApiModelStrategy[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/modelStrategy`,
        {
          headers: this.appHeaders,
          params: {
            withIsInEnglish: overrideCanReadEnglish ?? false,
            efektika
          }
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

    // GET /api/v1/collaborator/{collaboratorId}/clientAccount/{clientAccountId}/modelStrategy
    public getConsultClientModelStrategies(clientId: number | string): Observable<ApiModelStrategy[]> {
      return this.http
        .get<ApiModelStrategy[]>(
          `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/collaborator/${this.loggedConsult?.collaboratorId}/clientAccount/${clientId}/modelStrategy`,
          {
            headers: this.appHeaders,
            params: { efektika: true }
          },
        ).pipe(
          take(1),
          catchError(this.handleError.bind(this)),
        );
    }

    // GET /api/v1/collaborator/client/{clientId}/statistics
    public getConsultClientStatistics(clientId: number | string): Observable<ColClientAccountStatisticOut> {
      return this.http
        .get<ColClientAccountStatisticOut>(
          `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/collaborator/client/${clientId}/statistics`,
          {
            headers: this.appHeaders,
          },
        ).pipe(
          take(1),
          catchError(this.handleError.bind(this)),
        );
    }

  // GET /api/v1/client/{clientAccountId}/transaction
  public getTransactions(options: PaginatedTransactionInput): Observable<ApiPagedTransaction> {
    if(options?.page) {
      options.page = options.page - 1; // api pages start at 0
    }

    return this.http
      .get<ApiPagedTransaction>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${options.clientId}/transaction`,
        {
          headers: this.appHeaders,
          params: this.toHttpParams(options),
        },
      ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/client/current
  public getUserDetail(): Observable<ApiClientAccountOut> {
    return this.http
      .get<ApiClientAccountOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/current`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/collaborator/current
  public getConsultDetail(): Observable<ApiColCollaboratorOut> {
    return this.http
      .get<ApiColCollaboratorOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/collaborator/current`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET api/v1/modelStrategy/{id}
  public getModelStrategy(id: string): Observable<ModelStrategyOrPortfolioInstrumentOut> {
    return this.http
      .get<ModelStrategyOrPortfolioInstrumentOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/modelStrategy/${id}`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/collaborator/{collaboratorId}/client/{clientAccountId}/modelStrategy/{id}
  public getClientModelStrategy(clientId: string | number, modelStrategyId: string | number): Observable<ModelStrategyOrPortfolioInstrumentOut> {
    return this.http
      .get<ModelStrategyOrPortfolioInstrumentOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/collaborator/${this.loggedConsult?.collaboratorId}/client/${clientId}/modelStrategy/${modelStrategyId}`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/collaborator/{collaboratorId}/client/{clientId}/modelPortfolio/{riskScore}
  public getClientsModelPortfolio(clientId: number | string, riskScore: number | string): Observable<ApiModelPortfolioOut> {
    return this.http
      .get<ApiModelPortfolioOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/collaborator/${this.loggedConsult?.collaboratorId}/client/${clientId}/modelPortfolio/${riskScore}`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/collaborator/{collaboratorId}/client/{clientId}/modelPortfolio/{riskScore}/chart
  public getClientsModelPortfolioChart(params: ClientModelPortfolioChartInput): Observable<ApiModelPortfolioChartOut> {
    return this.http
      .post<ApiModelPortfolioChartOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/collaborator/${this.loggedConsult?.collaboratorId}/client/${params.clientId}/modelPortfolio/${params.riskProfile}/chart`,
        {
          weeksBetweenInvestments: params.depositRegularity.value,
          yearsToInvest: params.timeOfInvestment,
          initialDeposit: params.oneTimeDeposit,
          regularDeposit: params.regularDeposit
        },
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/modelPortfolio/{riskScore}/chart
  public getModelPortfolioChartForSuggestion(params: PdfDataInput['investmentSettingInput']): Observable<ApiModelPortfolioChartOut> {
    return this.http
      .post<ApiModelPortfolioChartOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/modelPortfolio/${params!.riskScore}/chart`,
        {
          weeksBetweenInvestments: params!.weeksBetweenInvestments.numeric,
          yearsToInvest: params!.totalYearsToInvest,
          initialDeposit: params!.initialDeposit,
          regularDeposit: params!.regularDeposit
        },
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/strategy/model-chart
  public getStrategyModelChart(options: ModelStrategyChartInput): Observable<ApiModelPortfolioChartOut> {
    return this.http
      .post<ApiModelPortfolioChartOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/strategy/model-chart`,
        options,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientId}/portfolio/{portfolioId}/model-portfolio
  public getInvestmentDetail(params: ClientRequestInput): Observable<PortfolioOut> {
    return this.http
      .get<PortfolioOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${params.clientId}/portfolio/${params.portfolioId}/model-portfolio`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v2/collaborator/{id}/fee-config
  public getWorkplaceFeeConfig(): Observable<FeeConfigOut[]> {
    return this.http
      .get<FeeConfigOut[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v2/collaborator/${this.loggedConsult?.collaboratorId}/fee-config`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/document
  public getDocuments(params: DocumentsRequestInput, clientId: number | string): Observable<PagedResultDocumentOut> {
    params.page = +params.page - 1; // api pages start at 0

    return this.http
      .get<PagedResultDocumentOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${clientId}/document`,
        {
          headers: this.appHeaders,
          params: this.toHttpParams(params),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientAccountId}/document/{documentId}/link
  public generateDocumentLink(documentId: number, clientAccountId: number): Observable<{link: string}> {
    return this.http
      .post<{link: string}>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${clientAccountId}/document/${documentId}/link`,
        {
          resolveAll: true,
        },
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/deposit
  public getMoneyDeposit(params: ClientRequestInput): Observable<PortfolioDepositOut> {
    return this.http
      .get<PortfolioDepositOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${params.clientId}/portfolio/${params.portfolioId}/deposit`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v2/client/{clientAccountId}/portfolio/{portfolioId}/fee
  public getPortfolioFees(params: ClientRequestInput): Observable<PortfolioFeeConfigsOut> {
    return this.http
      .get<PortfolioFeeConfigsOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v2/client/${params.clientId}/portfolio/${params.portfolioId}/fee`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/portfolio-instrument
  public getModelPortfolioInstruments(params: ClientRequestInput): Observable<PortfolioInstrumentSummaryOut> {
    return this.http
      .get<PortfolioInstrumentSummaryOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${params?.clientId}/portfolio/${params.portfolioId}/portfolio-instrument`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/strategy/limit
  public getStrategyLimits(): Observable<StrategyLimitOut> {
    return this.http
      .get<StrategyLimitOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/strategy/limit`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/modelPortfolio/{riskScore}
  public getModelPortfolioPublic(riskScore: number): Observable<ApiModelPortfolioOut> {
    return this.http
    .get<ApiModelPortfolioOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/modelPortfolio/' + riskScore,
      {
        headers: this.appHeaders,
      },
    )
    .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/collaborator/summary
  public getWorkplaceClientsSummary(): Observable<ColSummaryOut> {
    return this.http
      .get<ColSummaryOut>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/collaborator/summary',
        {
          headers: this.appHeaders,
        },
      ).pipe(
          take(1),
      );
  }

  // GET /api/v2/collaborator/client
  public getWorkplaceClientsList(params: ColClientsInput): Observable<PagedResultColClientAccountOverviewOut> {
    params.page = params.page - 1; // api pages start at 0

    return this.http
      .get<PagedResultColClientAccountOverviewOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v2/collaborator/client`,
        {
          headers: this.appHeaders,
          params: this.toHttpParams(params),
        },
      ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

    // GET /api/v1/collaborator/client-missing-deposit
  public getWorkplaceClientsMissingRegularDepositsList(params: ColClientsMissingDepositInput): Observable<ColClientMissingRegularPage> {
    params.page = params.page - 1; // api pages start at 0

    return this.http
      .get<ColClientMissingRegularPage>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/collaborator/client-missing-deposit`,
        {
          headers: this.appHeaders,
          params: this.toHttpParams(params),
        },
      ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/collaborator/client/near-target-amount
  public getWorkplaceExpiringDeposit(params: ColClientsInput): Observable<ColClientExpiringContractsPage> {
    params.page = params.page - 1; // api pages start at 0

    return this.http
      .get<ColClientExpiringContractsPage>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/collaborator/client/near-target-amount`,
        {
          headers: this.appHeaders,
          params: this.toHttpParams(params),
        },
      ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/collaborator/{collaboratorId}/info
  public getWorkplaceConsultInfo(): Observable<CollaboratorOut> {
    return this.http
      .get<CollaboratorOut>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/collaborator/' + this.loggedConsult?.collaboratorId + '/info',
        {
          headers: this.appHeaders,
        },
      ).pipe(
          take(1),
      );
  }

  // GET /api/v1/collaborator/{collaboratorId}/photo
  public getWorkplaceConsultImg(): Observable<Blob> {
    return this.http
      .get<Blob>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/collaborator/'+ this.loggedConsult?.collaboratorId +'/photo',
        {
          responseType: 'blob' as 'json',
          headers: {
            'Authorization': 'Bearer ' + this.userService.bearer,
            'accept': 'image/jpeg',
          },
        },
      ).pipe(
          take(1),
      );
  }

  // GET /api/v1/collaborators/{collaboratorId}/regular-withdrawal-draft/eligible-clients
  public getRegularWithdrawalEligibleClients(params: ColClientsRegularWithdrawalAvailableInput): Observable<PagedResultColClientAccountOverviewOut> {
    params.page = params.page - 1; // api pages start at 0
    return this.http
      .get<PagedResultColClientAccountOverviewOut>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/collaborators/' + this.loggedConsult?.collaboratorId + '/regular-withdrawal-draft/eligible-clients',
        {
          headers: this.appHeaders,
          params: this.toHttpParams(params),
        },
      ).pipe(
          take(1),
      );
  }

  // POST /api/v1/collaborator/{collaboratorId}/regular-withdrawal-draft
  public postDraftRegularWithdrawal(body: RegularWithdrawalDraftIn): Observable<void> {
    return this.http
    .post<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/collaborator/' + this.loggedConsult?.collaboratorId + '/regular-withdrawal-draft',
      body,
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/regular-withdrawal/limit
  public getWithdrawalLimits(input: WithdrawalDraftLimitsIn): Observable<ApiRegularWithdrawalLimit> {
    return this.http
      .get<ApiRegularWithdrawalLimit>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/client/' + input.clientId + '/portfolio/' + input.portfolioId + '/regular-withdrawal/limit',
        {
          headers: this.appHeaders,
          params: {
            frequency: input.frequency,
          }
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientId}/portfolio/{portfolioId}/bank-account
  public getAvailableBankAccounts(input: BankAccountsIn): Observable<ClientBankAccountOut[]> {
    return this.http
      .get<ClientBankAccountOut[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${input.clientId}/portfolio/${input.portfolioId}/bank-account`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/temporary-document/investment-info
  public generatePaymentInfoPdf(params: ClientRequestInput): Observable<GenerateDocumentLinkOut> {
    return this.http
    .post<GenerateDocumentLinkOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/client/' + (params.clientId) + '/portfolio/' + params.portfolioId + '/temporary-document/investment-info',
      {},
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }


  // POST /api/v1/temporary-document/transaction/export
  public generateTransactionsExport(params: ExportTransactionsIn): Observable<GenerateDocumentLinkOut> {
    return this.http
    .post<GenerateDocumentLinkOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/temporary-document/transaction/export',
      {},
      {
        headers: this.appHeaders,
        params: { ...params },
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/temporary-document/employer-payment
  public employerPaymentPdf(params: ClientRequestInput): Observable<{ link: string }> {
    return this.http
    .post<{ link: string }>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + (params.clientId) + '/portfolio/' + params.portfolioId + '/temporary-document/employer-payment',
      {},
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/temporary-document/investment-scenario
  public investmentSuggestionPdf(body: InvestmentSuggestionPdfInput): Observable<{ link: string }> {
    return this.http
    .post<{ link: string }>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/temporary-document/investment-scenario',
      body,
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/collaborator/{collaboratorId}/clientAccount/{clientId}/allow-opf-transfer
  public allowOpfTransfer(clientId: number): Observable<ColClientAccountOpfTransferOut> {
    return this.http
    .post<ColClientAccountOpfTransferOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/collaborator/'+ this.loggedConsult?.collaboratorId +'/clientAccount/' + clientId + '/allow-opf-transfer',
      {},
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/approveDipWithdrawal/{token}
  public approveDipWithdrawal(token: string): Observable<void> {
    return this.http
    .post<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/approveDipWithdrawal/' + token,
      {},
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/collaborator/{collaboratorId}/client/{clientId}/fee-settings
  public getFrameworkFees(clientId: string | number): Observable<FrameworkContractFeeSettingsOut[]> {
    return this.http
    .get<FrameworkContractFeeSettingsOut[]>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/collaborator/' + this.loggedConsult?.collaboratorId + '/client/' + clientId + '/fee-settings',
      {
        headers: this.appHeaders,
      },
    ).pipe(
        take(1),
    );
  }

  // GET /api/v1/client/{clientAccountId}/regular-withdrawal
  public getRegularWithdrawals(clientId: string | number): Observable<ApiRegularWithdrawal[]> {
    return this.http
      .get<ApiRegularWithdrawal[]>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/client/' + clientId + '/regular-withdrawal',
        {
          headers: this.appHeaders,
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v3/strategy/backcasting-chart
  public getNewInvestmentHistoryChart(instrumentValues: Record<string, number>, periodInMonths: 12 | 36 | 60): Observable<InstrumentPredictionOut> {
    return this.http
    .post<InstrumentPredictionOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v3/strategy/backcasting-chart',
      {
        isinToWeightMap: instrumentValues,
        graphSize: 'SMALL',
        periodInMonths,
      },
      {
        headers: this.appHeaders,
      },
    ).pipe(
        take(1),
    );
  }

  // POST /api/v1/security/loginBCAS-collaborator
  public backendLoginConsult(code: string, redirectUri: string): Observable<AccessToken> {
    return this.http
    .post<AccessToken>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/security/loginBCAS-collaborator',
      {
        code,
        redirectUri
      },
    )
    .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/collaborator/{collaboratorId}/clientAccount/{clientAccountId}
  public getConsultClientDetail(clientAccountId: number | string): Observable<ColClientAccountDetailOut> {
    return this.http
    .get<ColClientAccountDetailOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/collaborator/' + this.loggedConsult?.collaboratorId + '/clientAccount/' + clientAccountId,
      {
        headers: this.appHeaders,
      },
    ).pipe(
        take(1)
    );
  }

  // GET /api/v1/collaborator/{bcasCollaboratorId}/client/{bcasClientId}/bcas
  public getClientBwmId(bcasClientId: number, bcasCollaboratorId: number, bcasPortfolioVS?: string): Observable<ColCollaboratorIdClientAccountIdOut> {
    return this.http
    .get<ColCollaboratorIdClientAccountIdOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/collaborator/' + bcasCollaboratorId + '/client/' + bcasClientId + '/bcas',
      {
        headers: this.appHeaders,
        params: {
          ...(bcasPortfolioVS ? { portfolioVS: bcasPortfolioVS } : {} ),
        }
      },
    ).pipe(
        take(1)
    );
  }

  // POST /api/v1/fee-calculation
  public calculateEntryFeeForNewInvestment(calcInput: EntryFeeCalculationIn): Observable<EntryFeeCalculationWithAllRatiosOut> {
    return this.http
    .post<EntryFeeCalculationWithAllRatiosOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/fee-calculation',
      calcInput,
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      // TODO: ask backend to make all field in the dto @NotNull (now many are nullable) so we can remove this
      map(response => ({
        ...Object.fromEntries(
          Object.entries(response).map(
            ([k, v]) => [k, (v !== null || k === 'entryFeeType') ? v : 0]
          )
        )
      }) as EntryFeeCalculationWithAllRatiosOut),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/security/refresh
  public backendRefreshAuth(refreshToken: string): Observable<AccessToken> {
    return this.http
    .post<AccessToken>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/security/refresh',
      {
        refreshToken
      },
    )
    .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/all
  public getAllInvestments(clientAccountId: string | number): Observable<AllInvestmentsListItem[]> {
    return this.http
    .get<AllInvestmentsListItem[]>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + clientAccountId + '/portfolio/all',
      {
        headers: this.appHeaders,
      },
    ).pipe(
        take(1),
    );
  }

  // GET /api/v1/security/logout
  public logoutBackend(): Observable<{ idToken: string }> {
    return this.http
    .get<{ idToken: string }>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/security/logout',
      {
        headers: this.appHeaders,
      },
    ).pipe(take(1));
  }

  // GET /api/v1/collaborator/{collaboratorId}/draftPortfolio
  public getCollaboratorDraftPortfolios(params: CollaboratorDraftPortfoliosInput): Observable<PagedResultDraftPortfolioOverviewItemOut> {
    const { page, ...rest } = params;
    return this.http
      .get<PagedResultDraftPortfolioOverviewItemOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/collaborator/${this.loggedConsult?.collaboratorId}/draftPortfolio`,
        {
          headers: this.appHeaders,
          params: this.toHttpParams({ ...rest, page: page - 1 }),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/collaborator/{collaboratorId}/strategy-template
  public getDraftTemplatesList(params: TemplateDraftsListInput): Observable<PagedResultStrategyTemplateOverviewOut> {
    params.page = +params.page - 1;
    return this.http
      .get<PagedResultStrategyTemplateOverviewOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/collaborator/${this.loggedConsult?.collaboratorId}/strategy-template`,
        {
          headers: this.appHeaders,
          params: this.toHttpParams(params),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

    // GET /api/v1/collaborator/{collaboratorId}/strategy-template
    public getDraftTemplatesListForClient(clientAccountId: number, params: TemplateDraftsListInput): Observable<PagedResultStrategyTemplateOverviewOut> {
      params.page = +params.page - 1;
      return this.http
        .get<PagedResultStrategyTemplateOverviewOut>(
          `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/collaborator/${this.loggedConsult?.collaboratorId}/clientAccount/${clientAccountId}/strategy-template`,
          {
            headers: this.appHeaders,
            params: this.toHttpParams(params),
          },
        ).pipe(
          take(1),
          catchError(this.handleError.bind(this)),
        );
    }

  private handleError(error: HttpErrorResponse): Observable<never> {
    return throwError(() => error);
  }

  // GET /api/v1/collaborator/{collaboratorId}/draftPortfolio/{draftPortfolioId}
  public getDraftPortfolioDetail(draftPortfolioId: number, params?: DraftPortfolioDetailInput): Observable<DraftPortfolioDetail> {
    return this.http
      .get<DraftPortfolioDetail>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/collaborator/' + this.loggedConsult?.collaboratorId + '/draftPortfolio/' + draftPortfolioId,
        {
          headers: this.appHeaders,
          params: { ...(params ?? {}) },
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/collaborator/{collaboratorId}/strategy-template
  public createDraftTemplate(input: DraftTemplateIn): Observable<DraftTemplateOut> {
    return this.http
      .post<DraftTemplateOut>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) +  'v1/collaborator/' + this.loggedConsult?.collaboratorId + '/strategy-template',
        input,
        {
          headers: this.appHeaders,
        }
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/collaborator/{collaboratorId}/strategy-template/{templateId}
  public getDraftTemplateDetail(draftTemplateId: number): Observable<StrategyTemplateDetailOut> {
    return this.http
      .get<StrategyTemplateDetailOut>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) +  'v1/collaborator/' + this.loggedConsult?.collaboratorId + '/strategy-template/' + draftTemplateId,
        {
          headers: this.appHeaders,
        }
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // PUT /api/v1/collaborator/{collaboratorId}/strategy-template/{templateId}
  public updateDraftTemplateDetail(input: DraftTemplateIn, draftTemplateId: number): Observable<StrategyTemplateDetailOut> {
    return this.http
      .put<StrategyTemplateDetailOut>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) +  'v1/collaborator/' + this.loggedConsult?.collaboratorId + '/strategy-template/' + draftTemplateId,
        input,
        {
          headers: this.appHeaders,
        }
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // DELETE /api/v1/collaborator/{collaboratorId}/strategy-template/{templateId}
  public deleteDraftTemplateDetail(draftTemplateId: number): Observable<void> {
    return this.http
      .delete<void>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) +  'v1/collaborator/' + this.loggedConsult?.collaboratorId + '/strategy-template/' + draftTemplateId,
        {
          headers: this.appHeaders,
        }
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }
}
